import React, { useState, useMemo } from 'react';
import { reportsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MultiSelect } from './MultiSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, Download, Users, UserPlus, ChevronRight, Info } from 'lucide-react';
import { toast } from 'sonner';

// "New Admission Report — there will be a start date filter, the result will be
// Branch, Class, No. of students." (2026-09-03)
//
// Separate from the New Admissions dashboard, which counts intake per TERM. This
// one counts heads admitted from a chosen calendar date and groups them by branch
// and class — the shape used for a joining register.
//
// A student is counted in the window their Date of Joining falls in, whatever
// their standing today: someone who joined in August and has since left was still
// an admission. The caveat line under the table says so rather than leaving the
// number open to being read either way.

const num = (n) => Number(n || 0).toLocaleString();

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => columns.map((c) => esc(c.get(r))).join(','))].join('\n');
};

const downloadBlob = (name, payload, type) => {
  const blob = payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const STUDENT_COLUMNS = [
  { label: 'Student ID', get: (r) => r.student_id },
  { label: 'Student', get: (r) => r.name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Date of Joining', get: (r) => r.date_of_joining },
  { label: 'Joining Term', get: (r) => [r.joining_year, r.joining_term].filter(Boolean).join(' ') },
  { label: 'Parent/Guardian', get: (r) => r.parent_guardian },
  { label: 'Parent Phone', get: (r) => r.parent_phone },
  { label: 'Status', get: (r) => r.status },
];

const NewAdmissionReport = ({ configBranches = [], configClasses = [] }) => {
  const { user, isAdmin, userBranches } = useAuth();
  const branchOptions = isAdmin ? configBranches : (userBranches || []);
  // Mirrors require_roles on the export endpoint.
  const canExport = ['admin', 'manager'].includes(user?.role);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // What the shown data was fetched with — the drill-down and the export must use
  // this, not filters the user may have changed since Submit.
  const [applied, setApplied] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  const buildParams = () => ({
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(branches.length ? { branch: branches.join(',') } : {}),
    ...(classes.length ? { current_class: classes.join(',') } : {}),
  });

  const submit = async () => {
    if (!startDate) {
      toast.error('Pick a start date first');
      return;
    }
    if (endDate && endDate < startDate) {
      toast.error('End date is before the start date');
      return;
    }
    setLoading(true);
    const params = buildParams();
    try {
      const res = await reportsApi.newAdmissionReport(params);
      setData(res.data);
      setApplied(params);
      setDrill(null);
      setDrillData(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load new admission report');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!applied) return;
    setExporting(true);
    try {
      const res = await reportsApi.exportNewAdmissionReportExcel(applied);
      const tag = [applied.start_date, applied.end_date].filter(Boolean).join('_to_');
      downloadBlob(`new_admission_report_${tag}.xlsx`, res.data);
      toast.success('Excel exported');
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'Only Admin and Manager can export reports'
        : 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Drill into one Branch x Class row, or a whole branch from its subtotal.
  const openDrill = async (cell) => {
    if (!applied) return;
    setDrill(cell);
    setDrillSearch('');
    setDrillData(null);
    setDrillLoading(true);
    try {
      const res = await reportsApi.newAdmissionReportStudents({
        ...applied,
        ...(cell.branch ? { branch: cell.branch } : {}),
        ...(cell.current_class ? { current_class: cell.current_class } : {}),
      });
      setDrillData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load students');
    } finally {
      setDrillLoading(false);
    }
  };

  const filteredDrillRows = useMemo(() => {
    const rows = drillData?.students || [];
    const q = drillSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.student_id, r.branch, r.current_class,
      r.parent_guardian, r.parent_phone]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [drillData, drillSearch]);

  // Memoised so the lastOfBranch map below isn't rebuilt on every render.
  const rows = useMemo(() => data?.rows || [], [data]);
  const branchTotals = data?.branch_totals || [];
  const totalOf = (b) => branchTotals.find((t) => t.branch === b)?.students || 0;

  // Where each branch's block starts, so the subtotal row lands after its classes.
  const lastOfBranch = useMemo(() => {
    const seen = {};
    rows.forEach((r, i) => { seen[r.branch] = i; });
    return seen;
  }, [rows]);

  const rangeLabel = data
    ? `${data.start_date || 'the earliest record'}${data.end_date ? ` to ${data.end_date}` : ' onwards'}`
    : '';

  return (
    <div className="space-y-4">
      {/* ---- Filters ---- */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-44" data-testid="adm-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-44" data-testid="adm-end-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Branch</Label>
              <MultiSelect options={branchOptions} selected={branches} onChange={setBranches}
                placeholder="All Branches" testId="adm-branch" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Class</Label>
              <MultiSelect options={configClasses} selected={classes} onChange={setClasses}
                placeholder="All Classes" testId="adm-class" />
            </div>
            <Button onClick={submit} disabled={loading} className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
              data-testid="adm-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <UserPlus className="w-4 h-4 mr-2" />}
              Submit
            </Button>
            {data && canExport && (
              <Button variant="outline" onClick={exportExcel} disabled={exporting}
                data-testid="adm-export">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
            )}
          </div>
          <p className="text-xs text-[#5C635F] mt-3">
            Counts students by their <span className="font-medium">Date of Joining</span>.
            Leave End Date empty for everything from the start date onwards.
          </p>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card className="border-[#D8D9E8]">
          <CardContent className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
            <UserPlus className="w-12 h-12 mb-4 opacity-50" />
            <p>Pick a start date, then hit Submit.</p>
            <p className="text-xs mt-1">Branch and Class are optional — leave them empty for all.</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* ---- KPIs ---- */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="border-[#3E3F96]/25 bg-[#3E3F96]/10">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">New Admissions</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(data.total_students)}</p>
                <p className="text-xs text-[#5C635F] mt-1">{rangeLabel}</p>
              </CardContent>
            </Card>
            <Card className="border-[#5556A8]/25 bg-[#5556A8]/10">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Branches</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(data.branch_count)}</p>
              </CardContent>
            </Card>
            <Card className="border-[#D99C3B]/25 bg-[#D99C3B]/10">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-[#5C635F]">Classes</p>
                <p className="text-2xl font-bold text-[#1C1F1D]">{num(data.class_count)}</p>
              </CardContent>
            </Card>
          </div>

          {/* ---- Branch x Class ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-[#3E3F96]" />
                New Admissions by Branch and Class
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-sm text-[#5C635F] py-8 text-center">
                  No student joined on or after {data.start_date || 'that date'}
                  {data.end_date ? ` and on or before ${data.end_date}` : ''}.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Branch</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead className="text-right">No. of Students</TableHead>
                        <TableHead className="w-8" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r, i) => (
                        <React.Fragment key={`${r.branch}-${r.current_class}`}>
                          <TableRow className="cursor-pointer hover:bg-[#ECEDF5]/60"
                            onClick={() => openDrill(r)}
                            data-testid={`adm-row-${r.branch}-${r.current_class}`}>
                            {/* Branch printed once per block, so the eye follows the class list. */}
                            <TableCell className="font-medium">
                              {i === 0 || rows[i - 1].branch !== r.branch ? r.branch : ''}
                            </TableCell>
                            <TableCell>{r.current_class}</TableCell>
                            <TableCell className="text-right font-medium">{num(r.students)}</TableCell>
                            <TableCell className="text-[#5C635F]">
                              <ChevronRight className="w-4 h-4" />
                            </TableCell>
                          </TableRow>
                          {lastOfBranch[r.branch] === i && (
                            <TableRow className="bg-[#ECEDF5]/70 cursor-pointer hover:bg-[#ECEDF5]"
                              onClick={() => openDrill({ branch: r.branch })}
                              data-testid={`adm-subtotal-${r.branch}`}>
                              <TableCell className="font-semibold">{r.branch}</TableCell>
                              <TableCell className="text-[#5C635F] text-sm">All classes</TableCell>
                              <TableCell className="text-right font-semibold">
                                {num(totalOf(r.branch))}
                              </TableCell>
                              <TableCell className="text-[#5C635F]">
                                <ChevronRight className="w-4 h-4" />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                      <TableRow className="bg-[#3E3F96]/10 border-t-2 border-[#3E3F96]/30">
                        <TableCell className="font-bold">TOTAL</TableCell>
                        <TableCell />
                        <TableCell className="text-right font-bold" data-testid="adm-total">
                          {num(data.total_students)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* What the head-count does and does not mean. */}
              <p className="text-xs text-[#5C635F] mt-3 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  Every student whose Date of Joining falls in the range is counted, whatever
                  their standing now
                  {data.inactive_students > 0 && ` — ${num(data.inactive_students)} of these are inactive`}
                  {data.left_students > 0 && `, ${num(data.left_students)} have since left`}.
                  {data.no_joining_date > 0 && ` ${num(data.no_joining_date)} student(s) in scope have no joining date on file and cannot appear here.`}
                </span>
              </p>
            </CardContent>
          </Card>
        </>
      )}

      {/* ---- Drill-down ---- */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span>New admissions</span>
              {drill?.branch && <Badge variant="outline">{drill.branch}</Badge>}
              {drill?.current_class && <Badge variant="outline">{drill.current_class}</Badge>}
              {data && <Badge variant="outline">{rangeLabel}</Badge>}
              {drillData && <Badge className="bg-[#3E3F96]">{num(drillData.count)} students</Badge>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#5C635F]" />
              <Input className="pl-8" placeholder="Search name, ID, class, parent…"
                value={drillSearch} onChange={(e) => setDrillSearch(e.target.value)}
                data-testid="adm-drill-search" />
            </div>
            <Button variant="outline" size="sm"
              disabled={!filteredDrillRows.length}
              onClick={() => downloadBlob(
                `new_admissions_${drill?.branch || 'all'}_${drill?.current_class || 'all'}.csv`,
                toCsv(filteredDrillRows, STUDENT_COLUMNS), 'text/csv;charset=utf-8;')}>
              <Download className="w-4 h-4 mr-2" />CSV
            </Button>
          </div>

          <div className="overflow-auto flex-1">
            {drillLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
              </div>
            ) : filteredDrillRows.length === 0 ? (
              <p className="text-sm text-[#5C635F] py-8 text-center">No students to show.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {STUDENT_COLUMNS.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDrillRows.map((r) => (
                    <TableRow key={r.id}>
                      {STUDENT_COLUMNS.map((c) => (
                        <TableCell key={c.label} className="text-sm">{c.get(r) || '—'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewAdmissionReport;
